/**
 * Карточка игрока: всё про одного человека на одном экране.
 *
 * Появилась взамен четырёх столбцов в списке. Столбцы упирались в ширину
 * таблицы: каждый новый показатель приходилось втискивать между соседними,
 * а показателей больше — киллы и урон приходят из игровой службы Majestic.
 * Карточка снимает этот предел.
 *
 * Сюда приходят двумя путями: из базы игроков и щелчком по нику в статистике.
 * Второй путь и определил вид: сверху человек, дальше его боевые цифры, а
 * посещаемость наборов — в самом низу, мелким счётом. График по месяцам
 * владелец убрал: к статистике каптов он отношения не имеет.
 */

import { useMemo, type ReactNode } from 'react';
import { EVENT_RESULT_LABEL, type PlayerCard, type PlayerEventEntry } from '@ak/shared';

import { formatCount, formatDateTime } from '../lib/format.ts';
import { navigate, useLinkHandler } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import { CaptureList } from '../components/capture-list.tsx';
import { PlayerHero, Tile } from '../components/player-hero.tsx';
import { Badge, EmptyState, ErrorState, Panel, PanelHeader, Skeleton } from '../components/ui.tsx';

const number = (value: number): string => Math.round(value).toLocaleString('ru');

/**
 * Боевые цифры.
 *
 * Блок показан пустым, а не спрятан: место под цифры предусмотрено, и это
 * должно быть видно. Спрятанный блок читался бы как «такого не бывает», а
 * пустой — как «данных ещё нет», что и есть правда.
 */
function CombatTiles({ card, timezone }: { card: PlayerCard; timezone: string }): ReactNode {
  const combat = card.combat;

  const note =
    card.combatAbsence === 'not-configured'
      ? 'Источник боевых показателей не подключён. Появится ключ доступа — цифры встанут сюда сами.'
      : card.combatAbsence === 'no-data'
        ? 'Капты этого игрока ещё не сверены с игровой статистикой.'
        : `Источник: ${combat?.source ?? '—'} · обновлено ${formatDateTime(combat?.updatedAt ?? '', timezone)}`;

  const tiles: { label: string; value: string; hint?: string | undefined; tone?: string | undefined }[] =
    combat === null
      ? [
          { label: 'каптов', value: '—' },
          { label: 'побед', value: '—' },
          { label: 'киллов', value: '—' },
          { label: 'k/d', value: '—' },
          { label: 'урона', value: '—' },
          { label: 'урон/капт', value: '—' },
        ]
      : [
          { label: 'каптов', value: number(combat.captures) },
          {
            label: 'побед',
            value: combat.wins === null ? '—' : number(combat.wins),
            hint:
              combat.wins === null || combat.captures === 0
                ? undefined
                : `${String(Math.round((combat.wins / combat.captures) * 100))}% винрейт`,
          },
          {
            label: 'киллов',
            value: number(combat.kills),
            tone: 'var(--accent)',
            hint: `${combat.avgKills.toFixed(1)} за капт`,
          },
          {
            label: 'k/d',
            value: combat.kd === null ? '—' : combat.kd.toFixed(2),
            hint: combat.deaths === null ? undefined : `${number(combat.deaths)} смертей`,
          },
          { label: 'урона', value: number(combat.damage) },
          { label: 'урон/капт', value: number(combat.avgDamage) },
        ];

  return (
    <section className="mb-5">
      <h2 className="mb-2 text-sm font-semibold tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
        Цифры по каптам
      </h2>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map((tile) => (
          <Tile key={tile.label} {...tile} />
        ))}
      </div>
      <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        {note}
      </p>
    </section>
  );
}

/** Строка набора: тип, время, состав и итог. */
function EventRow({ entry, timezone }: { entry: PlayerEventEntry; timezone: string }): ReactNode {
  const tone =
    entry.status === 'cancelled'
      ? 'var(--text-muted)'
      : entry.result === 'win'
        ? 'var(--positive)'
        : entry.result === 'loss'
          ? 'var(--danger)'
          : 'var(--border-strong)';

  return (
    <button
      type="button"
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-xl py-3 pr-3 pl-4 text-left transition-colors hover:bg-[var(--surface-hover)]"
      style={{
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-raised)',
        borderLeft: `3px solid ${tone}`,
      }}
      onClick={() => {
        navigate(`/events/${String(entry.eventId)}`);
      }}
    >
      <span className="min-w-[8rem] flex-1">
        <span className="font-medium">{entry.eventType}</span>
        <span className="mt-0.5 block text-xs" style={{ color: 'var(--text-muted)' }}>
          {entry.startsAt === null ? 'без времени' : formatDateTime(entry.startsAt, timezone)}
        </span>
      </span>

      <Badge tone={entry.list === 'plus' ? 'accent' : 'neutral'}>
        {entry.list === 'plus' ? 'основа' : 'замена'}
      </Badge>

      <span className="w-28 text-right text-sm" style={{ color: tone }}>
        {entry.status === 'cancelled'
          ? 'отменён'
          : entry.result === null
            ? 'итог не отмечен'
            : EVENT_RESULT_LABEL[entry.result]}
      </span>
    </button>
  );
}

export function PlayerCardPage({ userId, timezone }: { userId: string; timezone: string }): ReactNode {
  const { data, loading, error, reload } = useResource<PlayerCard>(
    `/api/players/${userId}/card`,
    (event) => event.type === 'players.changed',
  );

  const onLinkClick = useLinkHandler();

  const captures = useMemo(
    () =>
      (data?.captures ?? []).map((entry) => ({
        captureId: entry.captureId,
        startedAt: entry.startedAt,
        opponent: entry.opponent,
        damage: entry.damage,
        kills: entry.kills,
        outcome: entry.outcome,
      })),
    [data],
  );

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading || data === null) {
    return <Skeleton rows={10} />;
  }

  const { player } = data;
  const rosters = player.mainCount + player.reserveCount;

  return (
    <>
      <div className="mb-4">
        <a
          href="/stats"
          onClick={onLinkClick}
          className="text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          ← к статистике
        </a>
      </div>

      <PlayerHero player={player} />

      <CombatTiles card={data} timezone={timezone} />

      <Panel className="mb-5 overflow-hidden">
        <PanelHeader
          title="Откаты"
          description={formatCount(data.rollbacks.length, 'откат', 'отката', 'откатов')}
          actions={
            data.rollbackThreadUrl === null ? undefined : (
              <a
                href={data.rollbackThreadUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg px-3 py-1.5 text-sm font-medium"
                style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
              >
                Ветка в Discord ↗
              </a>
            )
          }
        />
        <div className="px-5 pb-5">
          {data.rollbacks.length === 0 ? (
            <EmptyState
              title="Откатов пока нет"
              description="Всё, что игрок скинет в свою ветку откатов в Discord, появится здесь."
            />
          ) : (
            <div className="flex flex-col gap-2">
              {data.rollbacks.map((rollback) => (
                <a
                  key={`${rollback.postedAt}-${rollback.url}`}
                  href={rollback.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-xl px-4 py-3 transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{rollback.title ?? rollback.url}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {formatDateTime(rollback.postedAt, timezone)}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <section className="mb-5">
        <h2 className="mb-2 text-sm font-semibold tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
          Наборы
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="основа"
            value={number(player.mainCount)}
            hint={rosters === 0 ? undefined : `${String(Math.round((player.mainCount / rosters) * 100))}% записей`}
          />
          <Tile label="замена" value={number(player.reserveCount)} />
          <Tile label="mcl + vzz" value={number(player.mclVzzCount)} />
          <Tile label="capture" value={number(player.captureCount)} />
        </div>
      </section>

      <Panel className="mb-5 overflow-hidden">
        <PanelHeader title="Капты" description={formatCount(captures.length, 'капт', 'капта', 'каптов')} />
        <div className="px-5 pb-5">
          {captures.length === 0 ? (
            <EmptyState
              title="Каптов пока нет"
              description="Здесь появятся капты, в которых игрок был в составе семьи по данным игровой службы."
            />
          ) : (
            <CaptureList captures={captures} timezone={timezone} />
          )}
        </div>
      </Panel>

      <Panel className="overflow-hidden">
        <PanelHeader
          title="История наборов"
          description={formatCount(data.history.length, 'набор', 'набора', 'наборов')}
        />
        <div className="px-5 pb-5">
          {data.history.length === 0 ? (
            <EmptyState title="Наборов не было" description="Игрок ещё ни разу не записывался." />
          ) : (
            <div className="flex flex-col gap-2">
              {data.history.map((entry) => (
                <EventRow key={entry.eventId} entry={entry} timezone={timezone} />
              ))}
            </div>
          )}
        </div>
      </Panel>
    </>
  );
}
